import { useEffect } from "react"
import { CircularProgress, Box } from "@mui/material"
import { ConfigPollerQuery, fetchClientConfiguration } from "./configPollerApi"
import { useAppDispatch, useAppSelector } from "../store/storeHooks"
import { selectAuthToken, selectUserId, selectUserType } from "../login/loginSelectors"
import { ErrorDisplay } from "../../core/api/errorTypes"
import { useApiQuery } from "../../core/api/useApiQuery"
import { setConfigData } from "../config/configSlice"
import { useWaitIndicator } from "./useWaitIndicator"
import { setUser } from "../users/userSlice"
import { getAdminDetails, GetAdminDetailsResponse, getUserDetails, UserQuery } from "../users/userApi"
import { UserType } from "../login/loginTypes"

const WAIT_SIGNAL_TIMEOUT = 500
export const CLIENT_CONFIG_INTERVAL = 10000

function transformAdminData(data: GetAdminDetailsResponse) {
    return data.admin
}

export function ConfigPoller({ children }: { children: React.ReactNode }) {
    const dispatch = useAppDispatch()
    const token = useAppSelector(selectAuthToken)
    const userId = useAppSelector(selectUserId)
    const userType = useAppSelector(selectUserType)

    const configResult = useApiQuery(fetchClientConfiguration, {
        queryName: ConfigPollerQuery.CLIENT_CONFIGURATION,
        dependencies: {},
        enabled: Boolean(token),
        errorDisplay: ErrorDisplay.NONE,
    })

    const { data: userDetails, isSuccess: isSuccessUserDetails } = useApiQuery(getUserDetails, {
        queryName: UserQuery.USER_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
        //        refetchInterval: CLIENT_CONFIG_INTERVAL,
        enabled: userType === UserType.USER && Boolean(userId),
    })

    const { data: adminDetails, isSuccess: isSuccessAdminDetails } = useApiQuery(getAdminDetails, {
        queryName: UserQuery.ADMIN_DETAILS,
        dependencies: {
            userId: userId ?? 0,
        },
        enabled: userType !== UserType.USER && Boolean(userId),
        select: transformAdminData,
    })

    useEffect(() => {
        if (isSuccessAdminDetails && adminDetails.user) {
            dispatch(setUser(adminDetails?.user))
        }
        if (isSuccessUserDetails && userDetails.user) {
            dispatch(setUser(userDetails?.user))
        }
    }, [dispatch, userDetails?.user, adminDetails?.user, isSuccessUserDetails, isSuccessAdminDetails])

    const { showWaitIndicator } = useWaitIndicator(WAIT_SIGNAL_TIMEOUT)

    useEffect(() => {
        if (configResult.isSuccess) {
            dispatch(setConfigData(configResult.data.config))
        }
    }, [configResult.isSuccess, configResult.data, dispatch])

    if (!token) {
        return <>{children}</>
    }

    if (configResult.isLoading) {
        return showWaitIndicator ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        ) : null
    }

    return <>{children}</>
}
